import axios from 'axios';
import emailjs from 'emailjs-com';
import router from '@/router';
import CryptoJS from 'crypto-js';
import { getModuleSettingInstance } from '@/utils/auth';

class BaseApiService {
  localUrl = process.env.VUE_APP_LOCAL_URL;
  baseUrl = process.env.VUE_APP_BASE_URL;
  serviceUrl = process.env.VUE_APP_SERVICE_URL;
  afcUrl = process.env.VUE_APP_AFC_URL;
  workspaceUrl = process.env.VUE_APP_WORKSPACE_URL;

  constructor(endpointURL) {
    if (!endpointURL) throw new Error('Endpoint URL is not provided');
    this.endpointURL = endpointURL;

    this.endpointURL = endpointURL; // Setup endpoint URL
    this.instance = axios.create({}); // Create axios instance
  }

  async refreshToken() {
    const refreshResponse = await this.instance.post('/refresh-token', {
      refreshToken: sessionStorage.getItem('refreshToken'),
    });
    return refreshResponse.data.accessToken;
  }

  setupInterceptors() {
    this.instance.interceptors.request.use(
      async (config) => {
        const token = sessionStorage.getItem('accessToken');

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Refresh logic (if applicable):
        // - Check token expiration here (optional)
        // - If expired or nearing expiration, refresh token using a separate request
        // - Update access token in storage and headers
        // - Set a flag to avoid infinite refresh loops (optional)
        return config;
      },
      (error) => {
        // Handle request errors if needed
        return Promise.reject(error);
      }
    );

    this.instance.interceptors.response.use(
      (response) => {
        return response; // Do nothing on successful responses
      },

      async (error) => {
        const originalRequest = error.config;

        // Handle 401 (Unauthorized) errors:
        if (
          error.response &&
          error.response.status === 401 &&
          !originalRequest._retry
        ) {
          originalRequest._retry = true; // Flag prevents infinite loops

          try {
            if (sessionStorage.getItem('refreshToken')) {
              const newAccessToken = await this.refreshToken(); // Make a request to your refresh token endpoint
              sessionStorage.setItem('accessToken', newAccessToken); // Update access token in storage
              originalRequest.headers.Authorization = `Bearer ${newAccessToken}`; // Update access token in headers

              return this.instance(originalRequest); // Retry the original request with the new token
            } else {
              // Handle missing refresh token (e.g., redirect to login)
              router.push('/logout');
            }
          } catch (refreshError) {
            // Handle refresh error (e.g., redirect to login)
            router.push('/logout');
          }
        }

        // Re-throw the error if it's not a 401 or if refresh fails
        return Promise.reject(error);
      }
    );
  }

  handleErrors(error) {
    if (error.response) {
      // The request was made and the server responded with a status code
      console.error('Request failed with status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      // The request was made but no response was received
      console.error('No response received:', error.request);
    } else {
      // Something else happened while setting up the request
      console.error('Error setting up request:', error.message);
    }
  }
}

class MCartsApiService extends BaseApiService {
  constructor() {
    super('m-carts');
    this.setupInterceptors();
  }

  async fetch() {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
      return [];
    }
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async update(payload) {
    try {
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async getByQueryString(queryString) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}${queryString}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
      return [];
    }
  }

  async getCartById(id) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}/${id}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async getCartByParams(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async saveCartDetails(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async updateCartDetails(id, data) {
    try {
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${id}`,
        data
      );

      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async delete(id) {
    try {
      const response = await this.instance.delete(
        `${this.serviceUrl}/${this.endpointURL}/${id}`
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class MCartItemsApiService extends BaseApiService {
  constructor() {
    super('m-cart-items');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
          params: params,
        }
      );
      return response.data.data;
    } catch (err) {
      this.handleErrors(err);
      return [];
    }
  }

  async saveCartItem(data) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        data
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async delete(id) {
    try {
      const response = await this.instance.delete(
        `${this.serviceUrl}/${this.endpointURL}/${id}`
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class MPRApprovalsApiService extends BaseApiService {
  constructor() {
    super('m-pr-approvals');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async create(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async update(payload) {
    try {
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async delete(id) {
    try {
      const response = await this.instance.delete(
        `${this.serviceUrl}/${this.endpointURL}/${id}`
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class MPRApprovalItemsApiService extends BaseApiService {
  constructor() {
    super('m-pr-approval-items');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
          params: params,
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async getByQueryString(queryString) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}${queryString}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async create(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async update(payload) {
    try {
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async delete(id) {
    try {
      const response = await this.instance.delete(
        `${this.serviceUrl}/${this.endpointURL}/${id}`
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class MPRApprovalCommentsApiService extends BaseApiService {
  constructor() {
    super('m-pr-approval-comments');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
          params: params,
        }
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async create(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async update(payload) {
    try {
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async delete(id) {
    try {
      const response = await this.instance.delete(
        `${this.serviceUrl}/${this.endpointURL}/${id}`
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class MPOApprovalsApiService extends BaseApiService {
  constructor() {
    super('m-po-approvals');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async create(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async update(payload) {
    try {
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async delete(id) {
    try {
      const response = await this.instance.delete(
        `${this.serviceUrl}/${this.endpointURL}/${id}`
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class MPOApprovalItemsApiService extends BaseApiService {
  constructor() {
    super('m-po-approval-items');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async getByQueryString(queryString) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}${queryString}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async create(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async update(payload) {
    try {
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async delete(id) {
    try {
      const response = await this.instance.delete(
        `${this.serviceUrl}/${this.endpointURL}/${id}`
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class MPOApprovalCommentsApiService extends BaseApiService {
  constructor() {
    super('m-po-approval-comments');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
          params: params,
        }
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async create(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async update(payload) {
    try {
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async delete(id) {
    try {
      const response = await this.instance.delete(
        `${this.serviceUrl}/${this.endpointURL}/${id}`
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class MPOCancelApprovalsApiService extends BaseApiService {
  constructor() {
    super('m-po-cancel-approvals');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async getByQueryString(queryString) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}${queryString}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async create(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async update(payload) {
    try {
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async delete(id) {
    try {
      const response = await this.instance.delete(
        `${this.serviceUrl}/${this.endpointURL}/${id}`
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class MPOCancelApprovalItemsApiService extends BaseApiService {
  constructor() {
    super('m-po-cancel-approval-items');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async create(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async update(payload) {
    try {
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async delete(id) {
    try {
      const response = await this.instance.delete(
        `${this.serviceUrl}/${this.endpointURL}/${id}`
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class MPOCancelApprovalCommentsApiService extends BaseApiService {
  constructor() {
    super('m-po-cancel-approval-comments');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async create(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async update(payload) {
    try {
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async delete(id) {
    try {
      const response = await this.instance.delete(
        `${this.serviceUrl}/${this.endpointURL}/${id}`
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class MNonCartApprovalsApiService extends BaseApiService {
  constructor() {
    super('m-non-cart-approvals');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
          params: params,
        }
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async create(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async update(payload) {
    try {
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async delete(id) {
    try {
      const response = await this.instance.delete(
        `${this.serviceUrl}/${this.endpointURL}/${id}`
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class MNonCartApprovalItemsApiService extends BaseApiService {
  constructor() {
    super('m-non-cart-approval-items');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
          params: params,
        }
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async getByQueryString(queryString) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}${queryString}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async create(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async update(payload) {
    try {
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async delete(id) {
    try {
      const response = await this.instance.delete(
        `${this.serviceUrl}/${this.endpointURL}/${id}`
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class MNonCartApprovalCommentsApiService extends BaseApiService {
  constructor() {
    super('m-non-cart-approval-comments');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
          params: params,
        }
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async create(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async update(payload) {
    try {
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async delete(id) {
    try {
      const response = await this.instance.delete(
        `${this.serviceUrl}/${this.endpointURL}/${id}`
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class MNonCartsApiService extends BaseApiService {
  constructor() {
    super('m-non-carts');
    this.setupInterceptors();
  }
  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
          params: params,
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async getByQueryString(queryString) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}${queryString}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  // async getNonCartWithPagination(params) {
  //   try {
  //     if (params.status === "In-Process") {
  //       const waitingForPRApprovalStatus = "Waiting for Approval";
  //       const pendingStatus = "Pending";
  //       //const pendingDotStatus = "Pending.";
  //       //const errorStatus = "Error";
  //       const cancelledStatus = "Cancelled";

  //       const response = await this.instance.get(
  //         store.getters.isSpecialUser ?
  //           `${this.serviceUrl}/${this.endpointURL}?$skip=${params.skip}&$limit=${params.limit}&$or[0][status]=${waitingForPRApprovalStatus}&$or[1][status]=${pendingStatus}&$or[2][status]=${cancelledStatus}&is_active=${params.is_active}` :
  //           `${this.serviceUrl}/${this.endpointURL}?$skip=${params.skip}&$limit=${params.limit}&$or[0][status]=${waitingForPRApprovalStatus}&$or[1][status]=${pendingStatus}&$or[2][status]=${cancelledStatus}&requested_by_user=${params.userName}&is_active=${params.is_active}`,
  //         {
  //           headers: {
  // "Cache-Control": "no-cache"
  // },
  //         }
  //       );

  //       return response.data;
  //     } else if (params.status === "Draft") {
  //       const response = await this.instance.get(
  //         store.getters.isSpecialUser ?
  //           `${this.serviceUrl}/${this.endpointURL}?$skip=${params.skip}&$limit=${params.limit}&$or[0][status]=Draft&$or[1][status]=Rejected&$or[2][status]=Error&is_active=${params.is_active}` :
  //           `${this.serviceUrl}/${this.endpointURL}?$skip=${params.skip}&$limit=${params.limit}&$or[0][status]=Draft&$or[1][status]=Rejected&$or[2][status]=Error&requested_by_user=${params.userName}&is_active=${params.is_active}`,
  //         {
  //           headers: {
  // "Cache-Control": "no-cache"
  // },
  //         }
  //       );

  //       return response.data;
  //     } else if (params.status === "History") {
  //       const response = await this.instance.get(
  //         store.getters.isSpecialUser ?
  //           `${this.serviceUrl}/${this.endpointURL}?$skip=${params.skip}&$limit=${params.limit}&$or[0][status]=Approved&$or[1][status]=Sourcing Approved&is_active=${params.is_active}` :
  //           `${this.serviceUrl}/${this.endpointURL}?$skip=${params.skip}&$limit=${params.limit}&$or[0][status]=Approved&$or[1][status]=Sourcing Approved&requested_by_user=${params.userName}&is_active=${params.is_active}`,
  //         {
  //           headers: {
  // "Cache-Control": "no-cache"
  // },
  //         }
  //       );

  //       return response.data;
  //     }
  //   } catch (err) {
  //     this.handleErrors(err);
  //   }
  // }

  async getNonCarts(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
          params: params,
        }
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async getNonCartWithQueryString(queryString) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}${queryString}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async createNonCart(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async updateNonCart(payload) {
    try {
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );

      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async deleteNonCart(id) {
    try {
      const response = await this.instance.delete(
        `${this.serviceUrl}/${this.endpointURL}/${id}`
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class MNonCartItemsApiService extends BaseApiService {
  constructor() {
    super('m-non-cart-items');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
          params: params,
        }
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async getNonCartItems(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
          params: params,
        }
      );
      return response.data.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async getByQueryString(queryString) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}${queryString}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async createNonCartItem(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async updateNonCartItem(payload) {
    try {
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );

      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async deleteNonCartItem(id) {
    try {
      const response = await this.instance.delete(
        `${this.serviceUrl}/${this.endpointURL}/${id}`
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class MNonCartVendorsApiService extends BaseApiService {
  constructor() {
    super('m-non-cart-vendors');
    this.setupInterceptors();
  }

  async getNonCartVendor(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
          params: params,
        }
      );
      return response.data.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async createNonCartVendor(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async updateNonCartVendor(payload) {
    try {
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );

      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async deleteNonCartVendor(id) {
    try {
      const response = await this.instance.delete(
        `${this.serviceUrl}/${this.endpointURL}/${id}`
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class EmailingApiService extends BaseApiService {
  constructor() {
    super('emailing');
  }
  async sendEmail(payload) {
    try {
      const serviceId = payload.serviceId;
      const templateId = payload.templateId;
      const templateParams = payload.templateParams; // such as { name: "", subject: "" }
      const userId = payload.userId;

      const response = await emailjs.send(
        serviceId,
        templateId,
        templateParams,
        userId
      );

      return response;
    } catch (error) {
      //console.error("Email failed to send: ", err)
    }
  }
}

class UploadCatalogApiService extends BaseApiService {
  constructor() {
    super('upload-catalog');
    this.setupInterceptors();
  }

  async getMetadata() {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}/metadata`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
      throw err;
    }
  }

  async importData(payload) {
    try {
      // Use the create endpoint for now which provides validation
      // In the future, a dedicated import endpoint will handle actual data processing
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
      throw err;
    }
  }

  async downloadRetrySheet(data) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        data,
        { responseType: 'blob' }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
      throw err;
    }
  }

  async downloadPassedSheet(data) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        data,
        { responseType: 'blob' }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
      throw err;
    }
  }

  async downloadTemplate() {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}/template`,
        { responseType: 'blob' }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
      throw err;
    }
  }
}

class MDepartmentsApiService extends BaseApiService {
  constructor() {
    super('m-departments');
    this.setupInterceptors();
  }

  async getDepartments(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
          params: params,
        }
      );
      return response.data.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class MProductGLAccountsApiService extends BaseApiService {
  constructor() {
    super('m-product-gl-accounts');
    this.setupInterceptors();
  }

  async getProductGLAccounts(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
          params: params,
        }
      );
      return response.data.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class EmployeeApiService extends BaseApiService {
  constructor() {
    super('employees');
    this.setupInterceptors();
  }

  async getTBEmployee(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
          params: params,
        }
      );

      return response.data[0];
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async getEmployeeList(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
          params: params,
        }
      );

      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async getDirectManager(params) {
    try {
      const msInstance = getModuleSettingInstance();
      const response = await this.instance.get(
        `/${this.endpointURL}/GetDirectManager`,
        {
          headers: {
            'x-api-key': await msInstance.getApiKey(),
          },
          params: params,
        }
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }
  async getDirectApprovers(params) {
    try {
      const msInstance = getModuleSettingInstance();
      const response = await this.instance.get(
        `/${this.endpointURL}/GetDirectApprovers`,
        {
          headers: {
            'x-api-key': await msInstance.getApiKey(),
          },
          params: params,
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class ApproverListApiService extends BaseApiService {
  constructor() {
    super('Approval/Procurement');
  }

  async getApproverList(params) {
    try {
      const msInstance = getModuleSettingInstance();
      const response = await this.instance.get(
        `/${this.endpointURL}/GetApproverList`,
        {
          headers: {
            'x-api-key': await msInstance.getApiKey(),
          },
          params: params,
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class MVProductCategoriesApiService extends BaseApiService {
  constructor() {
    super('m-v-product-categories');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      // console.log("serviceUrl: ", this.serviceUrl);
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
          params: params,
        }
      );
      return response.data.data;
    } catch (err) {
      this.handleErrors(err);
      return [];
    }
  }

  async filterProductsWithPagination(params) {
    let response;
    try {
      const branchCodeParam = params.branchCode ? `&branch_code=${params.branchCode}` : '';
      if (params.categoryType === 'subcategory') {
        response = await this.instance.get(
          `${this.serviceUrl}/${
            this.endpointURL
          }?product_sub_category_description=${encodeURIComponent(
            params.categoryName
          )}&$skip=${params.skip}&$limit=${params.limit}${branchCodeParam}`,
          {
            headers: {
              'Cache-Control': 'no-cache',
            },
          }
        );
      } else if (params.categoryType === 'material_group') {
        response = await this.instance.get(
          `${this.serviceUrl}/${
            this.endpointURL
          }?material_group_description=${encodeURIComponent(
            params.categoryName
          )}&$skip=${params.skip}&$limit=${params.limit}${branchCodeParam}`,
          {
            headers: {
              'Cache-Control': 'no-cache',
            },
          }
        );
      }

      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async filterAllProductsWithPagination(params) {
    try {
      const branchCodeParam = params.branchCode ? `&branch_code=${params.branchCode}` : '';
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}?$skip=${params.skip}&$limit=${params.limit}${branchCodeParam}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );

      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async filterSearchProductsWithPagination(params) {
    try {
      const filteredText = params.searchText
        ? `&$or[0][product_title][$like]=*${params.searchText}*&$or[1][vendor_name][$like]=*${params.searchText}*&$or[2][product_code][$like]=*${params.searchText}*&$or[3][product_title_en][$like]=*${params.searchText}*&$or[4][vendor_code][$like]=*${params.searchText}*&$or[5][vendor_product_code][$like]=*${params.searchText}*`
        : '';

      const branchCodeParam = params.branchCode ? `&branch_code=${params.branchCode}` : '';
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}?$skip=${params.skip}&$limit=${params.limit}${branchCodeParam}${filteredText}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      let info = {
        total: 0,
        products: [],
      };
      if (response && response.data) {
        info.products = response.data.data || [];
        if (typeof response.data.total === 'number') {
          info.total = response.data.total;
        }
      }

      return info;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class MVProductCategoriesExpiredApiService extends BaseApiService {
  constructor() {
    super('m-v-product-categories-expired');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
          params: params,
        }
      );
      return response.data.data;
    } catch (err) {
      this.handleErrors(err);
      return [];
    }
  }

  async filterExpiredProductsWithPagination(params) {
    let response;
    try {
      if (params.categoryType === 'subcategory') {
        response = await this.instance.get(
          `${this.serviceUrl}/${
            this.endpointURL
          }?product_sub_category_description=${encodeURIComponent(
            params.categoryName
          )}&$skip=${params.skip}&$limit=${params.limit}&branch_code=${
            params.branchCode
          }`,
          {
            headers: {
              'Cache-Control': 'no-cache',
            },
          }
        );
      } else if (params.categoryType === 'material_group') {
        response = await this.instance.get(
          `${this.serviceUrl}/${
            this.endpointURL
          }?material_group_description=${encodeURIComponent(
            params.categoryName
          )}&$skip=${params.skip}&$limit=${params.limit}&branch_code=${
            params.branchCode
          }`,
          {
            headers: {
              'Cache-Control': 'no-cache',
            },
          }
        );
      }

      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async filterAllExpiredProductsWithPagination(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}?$skip=${params.skip}&$limit=${params.limit}&branch_code=${params.branchCode}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );

      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async filterSearchExpiredProductsWithPagination(params) {
    try {
      const filteredText = params.searchText
        ? `&$or[0][product_title][$like]=*${params.searchText}*&$or[1][vendor_name][$like]=*${params.searchText}*&$or[2][product_code][$like]=*${params.searchText}*&$or[3][product_title_en][$like]=*${params.searchText}*&$or[4][vendor_code][$like]=*${params.searchText}*&$or[5][vendor_product_code][$like]=*${params.searchText}*`
        : '';

      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}?$skip=${params.skip}&$limit=${params.limit}&branch_code=${params.branchCode}${filteredText}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      let info = {
        total: 0,
        products: [],
      };
      if (response && response.data) {
        info.products = response.data.data || [];
        if (typeof response.data.total === 'number') {
          info.total = response.data.total;
        }
      }

      return info;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class MVProductDetailsApiService extends BaseApiService {
  constructor() {
    super('m-v-product-details');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
          params: params,
        }
      );
      return response.data.data;
    } catch (err) {
      this.handleErrors(err);
      return [];
    }
  }
}

class VProductMediaApiService extends BaseApiService {
  constructor() {
    super('v-product-medias');
  }

  async getProductMedia(catalog_item_id) {
    try {
      //Returns array of objects that contains [src] && [thumbnail_path] field...
      const response = await this.instance.get(
        `${this.localUrl}/${this.endpointURL}?catalog_item_id=${catalog_item_id}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class ProductImagesApiService extends BaseApiService {
  constructor() {
    super('product-images');
    this.setupInterceptors();
  }

  async find(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
          params: params.query,
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
      return { success: false, files: [] };
    }
  }

  async uploadProductImages(formData) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class MProductFavoritesApiService extends BaseApiService {
  constructor() {
    super('m-product-favorites');
    this.setupInterceptors();
  }

  // For one product
  async findFavoriteProduct(userId, branchCode, catalogItemId) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}?user_id=${userId}&branch_code=${branchCode}&catalog_item_id=${catalogItemId}&is_active=true`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async filterFavoriteProductsWithPagination(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}?$skip=${params.skip}&$limit=${params.limit}&branch_code=${params.branchCode}&user_id=${params.userId}&$sort[id]=0&is_active=true`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );

      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  // For multiply products
  async getFavoriteProducts(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}?user_id=${params.userId}&branch_code=${params.branchCode}&is_active=true&$limit=5000`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async saveFavoriteProduct(payload) {
    try {
      const data = {
        user_id: payload.user_id,
        vendor_id: payload.vendor_id,
        vendor_name: payload.vendor_name,
        product_id: payload.product_id,
        product_code: payload.product_code,
        product_title: payload.product_title,
        product_price: payload.product_price,
        currency: payload.currency,
        unit: payload.unit,
        catalog_item_id: payload.catalog_item_id,
        product_thumbnail1_path: payload.product_thumbnail1_path,
        branch_code: payload.branch_code,
        branch_name: payload.branch_name,
      };
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        data
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async removeFavoriteProductById(id) {
    try {
      const response = await this.instance.delete(
        `${this.serviceUrl}/${this.endpointURL}/${id}`
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class MProductRecentlyViewsApiService extends BaseApiService {
  constructor() {
    super('m-product-recently-views');
    this.setupInterceptors();
  }

  async filterRecentlyViewProductsWithPagination(params) {
    try {
      const { includeExpired = false, ...requestParams } = params;
      const { skip, limit, branchCode, userId } = requestParams;

      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}?$skip=${skip}&$limit=${limit}&branch_code=${branchCode}&user_id=${userId}&$sort[id]=0&is_active=true`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );

      if (!includeExpired && response?.data?.data) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const filteredData = response.data.data.filter((item) => {
          if (!item.price_valid_to) {
            return true;
          }

          const expiryDate = new Date(item.price_valid_to);
          if (Number.isNaN(expiryDate.getTime())) {
            return true;
          }

          expiryDate.setHours(0, 0, 0, 0);
          return expiryDate >= today;
        });

        response.data = {
          ...response.data,
          data: filteredData,
          total: response.data.total,
        };
      }

      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async checkIsProductExistsInRecentlyViewed(
    userId,
    branchCode,
    catalogItemId
  ) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}?user_id=${userId}&branch_code=${branchCode}&catalog_item_id=${catalogItemId}&is_active=true`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async saveRecentlyViewProduct(payload) {
    try {
      const res = await this.checkIsProductExistsInRecentlyViewed(
        payload.user_id,
        payload.branch_code,
        payload.catalog_item_id
      );

      if (res.data && res.data.length) {
        await this.removeRecentlyViewProduct(res.data[0].id);
      }

      const data = {
        user_id: payload.user_id,
        vendor_id: payload.vendor_id,
        vendor_name: payload.vendor_name,
        product_id: payload.product_id,
        product_code: payload.product_code,
        product_title: payload.product_title,
        product_price: payload.product_price,
        currency: payload.currency,
        unit: payload.unit,
        catalog_item_id: payload.catalog_item_id,
        product_thumbnail1_path: payload.product_thumbnail1_path,
        branch_code: payload.branch_code,
        branch_name: payload.branch_name,
      };
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        data
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async removeRecentlyViewProduct(id) {
    try {
      const response = await this.instance.delete(
        `${this.serviceUrl}/${this.endpointURL}/${id}`
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class EMemoApiService extends BaseApiService {
  constructor() {
    super('Memo');
  }

  async getMemoList(params) {
    try {
      const msInstance = getModuleSettingInstance();
      const response = await this.instance.get(`/${this.endpointURL}/GetMemo`, {
        headers: {
          'x-api-key': await msInstance.getApiKey(),
        },
        params: params,
      });
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async getMemoItem(memoId) {
    try {
      const msInstance = getModuleSettingInstance();
      const response = await this.instance.get(
        `/${this.endpointURL}/GetMemoItem/${memoId}`,
        {
          headers: {
            'x-api-key': await msInstance.getApiKey(),
          },
        }
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async getDocTypeList(params) {
    try {
      const msInstance = getModuleSettingInstance();
      const response = await this.instance.get(
        `/${this.endpointURL}/DocTypeSearchDocTypeMappingParameters`,
        {
          headers: {
            'x-api-key': await msInstance.getApiKey(),
          },
          params: params,
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async searchMemo(params) {
    try {
      const msInstance = getModuleSettingInstance();
      const response = await this.instance.get(
        `/${this.endpointURL}/Search/GetByParameters`,
        {
          headers: {
            'x-api-key': await msInstance.getApiKey(),
          },
          params: params,
        }
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class EMarketing extends BaseApiService {
  constructor() {
    super('e-marketing');
  }
  async fetchMarketing(
    branchCode,
    fiscalYear,
    documentNumberFilter = '',
    eventNameFilter = '',
    emarketingId = ''
  ) {
    let soapData = [];
    const xml = `<?xml version="1.0" encoding="utf-8"?>
                  <soap:Envelope xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/">
                    <soap:Body>
                      <GetListProposalP2PViewModel xmlns="http://tempuri.org/">
                        <branchCode>${branchCode}</branchCode>
                        <FiscalYear>${fiscalYear}</FiscalYear>
                      </GetListProposalP2PViewModel>
                    </soap:Body>
                  </soap:Envelope>`;

    const response = await this.instance.post(
      `${this.workspaceUrl}/SMP.API/webservices/p2pwebservice.asmx`,
      xml,
      {
        headers: {
          'Content-Type': 'text/xml; charset=utf-8',
          SOAPAction: 'http://tempuri.org/GetListProposalP2PViewModel',
        },
      }
    );
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(response.data, 'text/xml');
    const items = xmlDoc.getElementsByTagName('ProposalP2PViewModel');
    for (let i = 0; i < items.length; i++) {
      const proposalP2PViewModel = xmlDoc.getElementsByTagName(
        'ProposalP2PViewModel'
      )[i];

      const id = proposalP2PViewModel.getElementsByTagName('Id')[0].textContent;
      const documentNumber =
        proposalP2PViewModel.getElementsByTagName('DocumentNumber')[0]
          .textContent;
      const title =
        proposalP2PViewModel.getElementsByTagName('Title')[0].textContent;
      const eventDate =
        proposalP2PViewModel.getElementsByTagName('EventDate')[0].textContent;
      const status =
        proposalP2PViewModel.getElementsByTagName('Status')[0].textContent;
      if (!documentNumberFilter && !eventNameFilter && !emarketingId) {
        soapData.push({
          id: parseInt(id),
          documentNumber: documentNumber,
          title: title,
          eventDate: eventDate,
          status: status,
        });
      } else if (documentNumberFilter && eventNameFilter && !emarketingId) {
        if (
          documentNumber.includes(documentNumberFilter) ||
          title.includes(eventNameFilter)
        ) {
          soapData.push({
            id: parseInt(id),
            documentNumber: documentNumber,
            title: title,
            eventDate: eventDate,
            status: status,
          });
        }
      } else if (documentNumberFilter && !eventNameFilter && !emarketingId) {
        if (documentNumber.includes(documentNumberFilter)) {
          soapData.push({
            id: parseInt(id),
            documentNumber: documentNumber,
            title: title,
            eventDate: eventDate,
            status: status,
          });
        }
      } else if (!documentNumberFilter && eventNameFilter && !emarketingId) {
        if (title.includes(eventNameFilter)) {
          soapData.push({
            id: parseInt(id),
            documentNumber: documentNumber,
            title: title,
            eventDate: eventDate,
            status: status,
          });
        }
      } else if (emarketingId) {
        if (emarketingId == id) {
          soapData.push({
            id: parseInt(id),
            documentNumber: documentNumber,
            title: title,
            eventDate: eventDate,
            status: status,
          });
        }
      }
    }
    return soapData;
  }

  async getListProposalP2PWBSViewModel(
    documentNumber,
    branchCodeParam,
    allWBS
  ) {
    const xml = `<?xml version="1.0" encoding="utf-8"?>
    <soap:Envelope xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/">
      <soap:Body>
        <GetListProposalP2PWBSViewModel xmlns="http://tempuri.org/">
          <documentNumber>${documentNumber}</documentNumber>
          <branchCode>${branchCodeParam}</branchCode>
          <allWBS>${allWBS}</allWBS>
        </GetListProposalP2PWBSViewModel>
      </soap:Body>
    </soap:Envelope>`;

    const response = await this.instance.post(
      `${this.workspaceUrl}/SMP.API/webservices/p2pwebservice.asmx`,
      xml,
      {
        headers: {
          'Content-Type': 'text/xml; charset=utf-8',
          SOAPAction: 'http://tempuri.org/GetListProposalP2PWBSViewModel',
        },
      }
    );

    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(response.data, 'text/xml');
    const items = xmlDoc.getElementsByTagName('ProposalP2PWBSViewModel');

    for (let i = 0; i < items.length; i++) {
      let proposalP2PViewModel = xmlDoc.getElementsByTagName(
        'ProposalP2PWBSViewModel'
      )[i];

      let id = proposalP2PViewModel.getElementsByTagName('Id')[0].textContent;
      let documentNumber =
        proposalP2PViewModel.getElementsByTagName('DocumentNumber')[0]
          .textContent;
      let url = proposalP2PViewModel.getElementsByTagName('URL')[0].textContent;
      let branchCode =
        proposalP2PViewModel.getElementsByTagName('BranchCode')[0].textContent;
      let branchName =
        proposalP2PViewModel.getElementsByTagName('BranchName')[0].textContent;
      let wbs = proposalP2PViewModel.getElementsByTagName('WBS')[0].textContent;
      if (branchCode === branchCodeParam) {
        return {
          id: parseInt(id),
          documentNumber: documentNumber,
          url: url,
          branchCode: branchCode,
          branchName: branchName,
          wbs: wbs,
        };
      }
    }
  }
}

class VCostCenterBudgetApiService extends BaseApiService {
  constructor() {
    super('v-cost-center-budgets');
    this.setupInterceptors();
  }

  async getCostCenterBudgets(budgetYear, costCenter) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}?budget_year=${budgetYear}&cost_center=${costCenter}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );

      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class VWBSBudgetApiService extends BaseApiService {
  constructor() {
    super('v-wbs-budgets');
    this.setupInterceptors();
  }

  async getWBSBudgets(budgetYear, wbsCode) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}?budget_year=${budgetYear}&wbs_code=${wbsCode}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class MWbsesApiService extends BaseApiService {
  constructor() {
    super('m-wbses');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async getByQueryString(queryString) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}${queryString}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class MAlfrescoProcurementsApiService extends BaseApiService {
  constructor() {
    super('m-alfresco-procurements');
    this.setupInterceptors();
  }

  async uploadFiles(payload) {
    let site = process.env.VUE_APP_AFC_SITE;
    let path = process.env.VUE_APP_AFC_PATH;
    if (payload.destination === 'blacklist') {
      path += `/Blacklist/${payload.additionalInformation.vendorCode} - ${payload.additionalInformation.vendorName}`;
    } else if (payload.destination === 'shopping-cart') {
      path += `/PR/${payload.additionalInformation.budgetYear}/${payload.additionalInformation.cartId}`;
    } else if (payload.destination === 'po-submit') {
      path += `/PO/${payload.additionalInformation.budgetYear}/${payload.additionalInformation.poId}`;
    } else if (payload.destination === 'po-receipt') {
      path += `/Receipt/${payload.additionalInformation.budgetYear}/${payload.additionalInformation.sapPoNumber}${payload.additionalInformation.branchCode}`;
    } else if (payload.destination === 'goods-receipt') {
      path += `/GR/${payload.additionalInformation.budgetYear}/${payload.additionalInformation.sapPoNumber}${payload.additionalInformation.branchCode}`;
    } else if (payload.destination === 'non-cart') {
      path += `/Sourcing/${payload.additionalInformation.budgetYear}/${payload.additionalInformation.nonCartId}`;
    } else if (payload.destination === 'sourcing') {
      path += `/ProcurementSourcing/${payload.additionalInformation.budgetYear}/${payload.additionalInformation.nonCartId}`;
    }

    const formData = new FormData();
    formData.append('property', "{ Div: 'T' }");
    formData.append('permission', '');
    formData.append('prefix', 'http://www.bgear.co.th/model/content/esd1.0');
    formData.append('documentType', 'esddoc');
    formData.append('path', path);
    formData.append('site', site);
    const filesInformation = [];
    for (let i = 0; i < payload.files.length; i++) {
      try {
        const timestamp = new Date().getTime().toString();
        formData.append(
          'fileName',
          payload.files[i].name.replace(/(\.[^.]+)$/, `_${timestamp}$1`)
        ); // Insert timestamp before the file extension
        formData.append('fileUpload', payload.files[i]);
        const response = await this.instance.post(
          `${this.afcUrl}/alfresco/s/bgear/blink/content/setContent`,
          formData,
          {
            params: {
              alf_ticket: payload.ticket,
            },
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            timeout: process.env.VUE_APP_FILE_UPLOADING_TIMEOUT,
          }
        );
        if (response && response.status === 200) {
          filesInformation.push({
            folder_noderef: response.data.folder_noderef,
            noderef: response.data.nodeRef,
            file_name: payload.files[i].name,
            time_stamp: timestamp,
            success: true,
          });
        } else {
          filesInformation.push({
            folder_noderef: 'Incorrect response',
            noderef: 'Incorrect response',
            file_name: payload.files[i].name,
            success: false,
          });
        }
      } catch (error) {
        filesInformation.push({
          folder_noderef: 'Catch error',
          noderef: 'Catch error',
          file_name: payload.files[i].name,
          success: false,
          remark: JSON.stringify(error),
        });
      }
    }
    return filesInformation;
  }

  async getContentById(ticket, id) {
    try {
      const response = await this.instance.get(
        `${this.afcUrl}/alfresco/s/bgear/blink/content/getContentById`,
        {
          params: {
            alf_ticket: ticket,
            nodeRef: id,
          },
          responseType: 'arraybuffer',
        }
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async getFilesInformation(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
        }
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async saveFileInformation(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async updateFileInformation(payload) {
    try {
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class VendorBoardsApiService extends BaseApiService {
  constructor() {
    super('vendor-boards');
  }

  async getVendorBoards(params) {
    try {
      const response = await this.instance.get(
        `${this.localUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async createVendorBoard(payload) {
    try {
      const response = await this.instance.post(
        `${this.localUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async updateVendorBoard(payload) {
    try {
      const response = await this.instance.patch(
        `${this.localUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class VendorInformationsApiService extends BaseApiService {
  constructor() {
    super('v-vendor-datas');
    this.setupInterceptors();
  }

  async getById(id) {
    try {
      let vendor;

      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}/${id}`
      );

      if (response.data && response.status === 200) {
        vendor = {
          id: response.data.id,
          vendor_id: response.data.id,
          vendor_code: response.data.vendor_code,
          vendor_branch_code: response.data.vendor_branch_code,
          created_by: response.data.created_by,
          created_date: response.data.created_date,
          updated_by: response.data.updated_by,
          updated_date: response.data.updated_date,
          is_active: response.data.is_active,
          remark: response.data.remark,
        };

        const [
          { data: getRegisterResponse },
          { data: getGeneralResponse },
          { data: getPaymentResponse },
          { data: getSapResponse },
        ] = await Promise.all([
          this.instance.get(
            `${this.serviceUrl}/v-register-informations?vendor_branch_code=${response.data.vendor_branch_code}`
          ),
          this.instance.get(
            `${this.serviceUrl}/v-general-informations?vendor_branch_code=${vendor.vendor_branch_code}`
          ),
          this.instance.get(
            `${this.serviceUrl}/v-payment-informations?vendor_branch_code=${vendor.vendor_branch_code}`
          ),
          this.instance.get(
            `${this.serviceUrl}/v-saps?vendor_branch_code=${vendor.vendor_branch_code}`
          ),
        ]);

        if (getRegisterResponse && getRegisterResponse.data.length) {
          const registerInformation = getRegisterResponse.data[0];

          vendor = {
            ...vendor,
            tax_id: registerInformation.vendor_code,
            sap_vendor_code: registerInformation.sap_vendor_code,
            vat_register: registerInformation.vat_register,
            vendor_type: registerInformation.vendor_type,
            register_country: registerInformation.register_country,
            branch_code: registerInformation.branch,
          };
        }

        if (getGeneralResponse && getGeneralResponse.data.length) {
          const generalInformation = getGeneralResponse.data[0];

          vendor = {
            ...vendor,
            branch_name: generalInformation.branch_name,
            title: generalInformation.title_value,
            vendor_name_th: generalInformation.vendor_name_th,
            house_number: generalInformation.house_number,
            // village_number: generalInformation.village_number,
            building_number: generalInformation.building_number,
            room_number: generalInformation.room_number,
            building: generalInformation.building,
            floor: generalInformation.floor,
            village: generalInformation.village,
            lane: generalInformation.lane,
            road: generalInformation.road,
            cities: generalInformation.cities,
            district: generalInformation.district,
            province: generalInformation.province,
            postal_code: generalInformation.postal_code,
            country_full: generalInformation.country_full,
            contact_information_contact_name:
              generalInformation.contect_information_contact_name,
            contact_information_telephone_number:
              generalInformation.contect_information_telephone_number,
            contact_information_telephone_number_ext:
              generalInformation.contect_information_telephone_number_ext,
            contact_information_mobile_phone_number:
              generalInformation.contect_information_mobile_phone_number,
            contact_information_email:
              generalInformation.contect_information_email,
            finance_contact_contact_name:
              generalInformation.finance_contact_contact_name,
            finance_contact_telephone_number:
              generalInformation.finance_contact_telephone_number,
            finance_contact_telephone_number_ext:
              generalInformation.finance_contact_telephone_number_ext,
            finance_contact_mobile_phone_number:
              generalInformation.finance_contact_mobile_phone_number,
            finance_contact_email: generalInformation.finance_contact_email,
            note: '',
            location: vendor.branch_code
              ? generalInformation.branch_name
                ? generalInformation.branch_name + ` (${vendor.branch_code})`
                : vendor.branch_code
              : '',
            company:
              vendor.sap_vendor_code && generalInformation.vendor_name_th
                ? vendor.sap_vendor_code +
                  ` ${generalInformation.vendor_name_th}`
                : '',
            address:
              generalInformation.country_full === 'TH (Thailand)'
                ? generalInformation.house_number +
                  ' ' +
                  generalInformation.building +
                  ' ' +
                  generalInformation.building_number +
                  ' ' +
                  generalInformation.room_number +
                  ' ' +
                  generalInformation.floor +
                  ' ' +
                  generalInformation.village +
                  ' ' +
                  generalInformation.lane +
                  ' ' +
                  generalInformation.road +
                  ' ' +
                  generalInformation.cities +
                  ' ' +
                  generalInformation.district +
                  ' ' +
                  generalInformation.province +
                  ' ' +
                  generalInformation.postal_code
                : generalInformation.house_number +
                  ' ' +
                  generalInformation.building +
                  ' ' +
                  generalInformation.building_number +
                  ' ' +
                  generalInformation.room_number +
                  ' ' +
                  generalInformation.floor +
                  ' ' +
                  generalInformation.village +
                  ' ' +
                  generalInformation.lane +
                  ' ' +
                  generalInformation.road +
                  ' ' +
                  generalInformation.cities +
                  ' ' +
                  generalInformation.district +
                  ' ' +
                  generalInformation.province +
                  ' ' +
                  generalInformation.postal_code +
                  ' ' +
                  generalInformation.country_full,
          };
        }

        if (getPaymentResponse && getPaymentResponse.data.length) {
          const paymentInformation = getPaymentResponse.data[0];

          vendor = {
            ...vendor,
            payment_term: paymentInformation.payment_term,
            payment_type: paymentInformation.payment_type,
            currency: paymentInformation.currency,
          };
        }

        if (getSapResponse && getSapResponse.data.length) {
          const sapInformation = getSapResponse.data[0];

          vendor = {
            ...vendor,
            payment_methods: sapInformation.payment_methods,
            place_to_receive: sapInformation.place_to_receive,
          };
        }
      }

      return vendor;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async getByCode(code) {
    try {
      let vendor;

      const { data: response } = await this.instance.get(
        `${this.serviceUrl}/v-register-informations?sap_vendor_code=${code}`
      );

      if (response && response.data.length) {
        vendor = {
          tax_id: response.data[0].vendor_code,
          sap_vendor_code: response.data[0].sap_vendor_code,
          vat_register: response.data[0].vat_register,
          vendor_type: response.data[0].vendor_type,
          register_country: response.data[0].register_country,
          branch_code: response.data[0].branch,
          vendor_code: response.data[0].vendor_code,
          vendor_branch_code: response.data[0].vendor_branch_code,
        };

        const [
          { data: getVendorData },
          { data: getGeneralResponse },
          { data: getPaymentResponse },
          { data: getSapResponse },
        ] = await Promise.all([
          this.instance.get(
            `${this.serviceUrl}/${this.endpointURL}?vendor_branch_code=${vendor.vendor_branch_code}`
          ),
          this.instance.get(
            `${this.serviceUrl}/v-general-informations?vendor_branch_code=${vendor.vendor_branch_code}`
          ),
          this.instance.get(
            `${this.serviceUrl}/v-payment-informations?vendor_branch_code=${vendor.vendor_branch_code}`
          ),
          this.instance.get(
            `${this.serviceUrl}/v-saps?vendor_branch_code=${vendor.vendor_branch_code}`
          ),
        ]);

        if (getVendorData && getVendorData.data.length) {
          const vendorData = getVendorData.data[0];

          vendor = {
            ...vendor,
            id: vendorData.id,
            vendor_id: vendorData.id,
            created_by: vendorData.created_by,
            created_date: vendorData.created_date,
            updated_by: vendorData.updated_by,
            updated_date: vendorData.updated_date,
            is_active: vendorData.is_active,
            remark: vendorData.remark,
          };
        }

        if (getGeneralResponse && getGeneralResponse.data.length) {
          const generalInformation = getGeneralResponse.data[0];

          vendor = {
            ...vendor,
            branch_name: generalInformation.branch_name,
            title: generalInformation.title_value,
            vendor_name_th: generalInformation.vendor_name_th,
            house_number: generalInformation.house_number,
            // village_number: generalInformation.village_number,
            building_number: generalInformation.building_number,
            room_number: generalInformation.room_number,
            building: generalInformation.building,
            floor: generalInformation.floor,
            village: generalInformation.village,
            lane: generalInformation.lane,
            road: generalInformation.road,
            cities: generalInformation.cities,
            district: generalInformation.district,
            province: generalInformation.province,
            postal_code: generalInformation.postal_code,
            country_full: generalInformation.country_full,
            contact_information_contact_name:
              generalInformation.contect_information_contact_name,
            contact_information_telephone_number:
              generalInformation.contect_information_telephone_number,
            contact_information_telephone_number_ext:
              generalInformation.contect_information_telephone_number_ext,
            contact_information_mobile_phone_number:
              generalInformation.contect_information_mobile_phone_number,
            contact_information_email:
              generalInformation.contect_information_email,
            finance_contact_contact_name:
              generalInformation.finance_contact_contact_name,
            finance_contact_telephone_number:
              generalInformation.finance_contact_telephone_number,
            finance_contact_telephone_number_ext:
              generalInformation.finance_contact_telephone_number_ext,
            finance_contact_mobile_phone_number:
              generalInformation.finance_contact_mobile_phone_number,
            finance_contact_email: generalInformation.finance_contact_email,
            note: '',
            location: vendor.branch_code
              ? generalInformation.branch_name
                ? generalInformation.branch_name + ` (${vendor.branch_code})`
                : vendor.branch_code
              : '',
            company:
              vendor.sap_vendor_code && generalInformation.vendor_name_th
                ? vendor.sap_vendor_code +
                  ` ${generalInformation.vendor_name_th}`
                : '',
            address:
              generalInformation.country_full === 'TH (Thailand)'
                ? generalInformation.house_number +
                  ' ' +
                  generalInformation.building +
                  ' ' +
                  generalInformation.building_number +
                  ' ' +
                  generalInformation.room_number +
                  ' ' +
                  generalInformation.floor +
                  ' ' +
                  generalInformation.village +
                  ' ' +
                  generalInformation.lane +
                  ' ' +
                  generalInformation.road +
                  ' ' +
                  generalInformation.cities +
                  ' ' +
                  generalInformation.district +
                  ' ' +
                  generalInformation.province +
                  ' ' +
                  generalInformation.postal_code
                : generalInformation.house_number +
                  ' ' +
                  generalInformation.building +
                  ' ' +
                  generalInformation.building_number +
                  ' ' +
                  generalInformation.room_number +
                  ' ' +
                  generalInformation.floor +
                  ' ' +
                  generalInformation.village +
                  ' ' +
                  generalInformation.lane +
                  ' ' +
                  generalInformation.road +
                  ' ' +
                  generalInformation.cities +
                  ' ' +
                  generalInformation.district +
                  ' ' +
                  generalInformation.province +
                  ' ' +
                  generalInformation.postal_code +
                  ' ' +
                  generalInformation.country_full,
          };
        }

        if (getPaymentResponse && getPaymentResponse.data.length) {
          const paymentInformation = getPaymentResponse.data[0];

          vendor = {
            ...vendor,
            payment_term: paymentInformation.payment_term,
            payment_type: paymentInformation.payment_type,
            currency: paymentInformation.currency,
          };
        }

        if (getSapResponse && getSapResponse.data.length) {
          const sapInformation = getSapResponse.data[0];

          vendor = {
            ...vendor,
            payment_methods: sapInformation.payment_methods,
            place_to_receive: sapInformation.place_to_receive,
          };
        }
      }

      return vendor;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async getVendors(params) {
    try {
      let vendors = [];

      const { data: response } = await this.instance.get(
        `${this.serviceUrl}/v-general-informations?vendor_name_th[$like]=*${params.vendor_name_th}*&$limit=${params.limit}&$skip=${params.skip}&is_active=${params.is_active}`
      );

      if (response && response.data.length) {
        for (let v of response.data) {
          const [
            { data: getRegisterResponse },
            { data: getVendorData },
            { data: getPaymentResponse },
            { data: getSapResponse },
          ] = await Promise.all([
            this.instance.get(
              `${this.serviceUrl}/v-register-informations?vendor_branch_code=${v.vendor_branch_code}`
            ),
            this.instance.get(
              `${this.serviceUrl}/${this.endpointURL}?vendor_branch_code=${v.vendor_branch_code}`
            ),
            this.instance.get(
              `${this.serviceUrl}/v-payment-informations?vendor_branch_code=${v.vendor_branch_code}`
            ),
            this.instance.get(
              `${this.serviceUrl}/v-saps?vendor_branch_code=${v.vendor_branch_code}`
            ),
          ]);

          let vendor = {
            vendor_branch_code: v.vendor_branch_code,
            branch_name: v.branch_name,
            title: v.title_value,
            vendor_name_th: v.vendor_name_th,
            house_number: v.house_number,
            // village_number: v.village_number,
            building_number: v.building_number,
            room_number: v.room_number,
            building: v.building,
            floor: v.floor,
            village: v.village,
            lane: v.lane,
            road: v.road,
            cities: v.cities,
            district: v.district,
            province: v.province,
            postal_code: v.postal_code,
            country_full: v.country_full,
            contact_information_contact_name:
              v.contect_information_contact_name,
            contact_information_telephone_number:
              v.contect_information_telephone_number,
            contact_information_telephone_number_ext:
              v.contect_information_telephone_number_ext,
            contact_information_mobile_phone_number:
              v.contect_information_mobile_phone_number,
            contact_information_email: v.contect_information_email,
            finance_contact_contact_name: v.finance_contact_contact_name,
            finance_contact_telephone_number:
              v.finance_contact_telephone_number,
            finance_contact_telephone_number_ext:
              v.finance_contact_telephone_number_ext,
            finance_contact_mobile_phone_number:
              v.finance_contact_mobile_phone_number,
            finance_contact_email: v.finance_contact_email,
            note: '',
            address:
              v.country_full === 'TH (Thailand)'
                ? v.house_number +
                  ' ' +
                  v.building +
                  ' ' +
                  v.building_number +
                  ' ' +
                  v.room_number +
                  ' ' +
                  v.floor +
                  ' ' +
                  v.village +
                  ' ' +
                  v.lane +
                  ' ' +
                  v.road +
                  ' ' +
                  v.cities +
                  ' ' +
                  v.district +
                  ' ' +
                  v.province +
                  ' ' +
                  v.postal_code
                : v.house_number +
                  ' ' +
                  v.building +
                  ' ' +
                  v.building_number +
                  ' ' +
                  v.room_number +
                  ' ' +
                  v.floor +
                  ' ' +
                  v.village +
                  ' ' +
                  v.lane +
                  ' ' +
                  v.road +
                  ' ' +
                  v.cities +
                  ' ' +
                  v.district +
                  ' ' +
                  v.province +
                  ' ' +
                  v.postal_code +
                  ' ' +
                  v.country_full,
          };

          if (getRegisterResponse && getRegisterResponse.data.length) {
            const registerInformation = getRegisterResponse.data[0];

            vendor = {
              ...vendor,
              tax_id: registerInformation.vendor_code,
              sap_vendor_code: registerInformation.sap_vendor_code,
              vat_register: registerInformation.vat_register,
              vendor_type: registerInformation.vendor_type,
              register_country: registerInformation.register_country,
              branch_code: registerInformation.branch,
              vendor_code: registerInformation.vendor_code,

              location: registerInformation.branch
                ? v.branch_name
                  ? v.branch_name + ` (${registerInformation.branch})`
                  : registerInformation.branch
                : '',
              company:
                registerInformation.sap_vendor_code && v.vendor_name_th
                  ? registerInformation.sap_vendor_code + ` ${v.vendor_name_th}`
                  : '',
            };
          }

          if (getVendorData && getVendorData.data.length) {
            const vendorData = getVendorData.data[0];

            vendor = {
              ...vendor,
              id: vendorData.id,
              status: vendorData.status,
              vendor_id: vendorData.id,
              created_by: vendorData.created_by,
              created_date: vendorData.created_date,
              updated_by: vendorData.updated_by,
              updated_date: vendorData.updated_date,
              is_active: vendorData.is_active,
              remark: vendorData.remark,
            };
          }

          if (getPaymentResponse && getPaymentResponse.data.length) {
            const paymentInformation = getPaymentResponse.data[0];

            vendor = {
              ...vendor,
              payment_term: paymentInformation.payment_term,
              payment_type: paymentInformation.payment_type,
              currency: paymentInformation.currency,
            };
          }

          if (getSapResponse && getSapResponse.data.length) {
            const sapInformation = getSapResponse.data[0];

            vendor = {
              ...vendor,
              payment_methods: sapInformation.payment_methods,
              place_to_receive: sapInformation.place_to_receive,
            };
          }

          vendors.push(vendor);
        }

        return {
          ...response,
          data: vendors,
        };
      }

      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class VVendorBranchesApiService extends BaseApiService {
  constructor() {
    super('v-vendor-branches');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async getById(id) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}/${id}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class VPaymentInformationsApiService extends BaseApiService {
  constructor() {
    super('v-payment-informations');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class VVendorInvoicesApiService extends BaseApiService {
  constructor() {
    super('v-vendor-invoices');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class BlacklistConditionsApiService extends BaseApiService {
  constructor() {
    super('blacklist-conditions');
  }

  async getBlacklistConditions(params) {
    try {
      const response = await this.instance.get(
        `${this.localUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async createBlacklistCondition(payload) {
    try {
      const response = await this.instance.post(
        `${this.localUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async updateBlacklistCondition(payload) {
    try {
      const response = await this.instance.patch(
        `${this.localUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class VendorBlacklistsApiService extends BaseApiService {
  constructor() {
    super('vendor-blacklists');
  }

  async getVendorBlacklists(params) {
    try {
      const response = await this.instance.get(
        `${this.localUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async createVendorBlacklist(payload) {
    try {
      const response = await this.instance.post(
        `${this.localUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async updateVendorBlacklist(payload) {
    try {
      const response = await this.instance.patch(
        `${this.localUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class VVendorBlacklistsApiService extends BaseApiService {
  constructor() {
    super('v-vendor-blacklists');
  }

  async getVVendorBlacklists(params) {
    try {
      const response = await this.instance.get(
        `${this.localUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async getVVendorBlacklistById(id) {
    try {
      const response = await this.instance.get(
        `${this.localUrl}/${this.endpointURL}/${id}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async getVVendorBlacklistsWithQueryString(queryString) {
    try {
      const response = await this.instance.get(
        `${this.localUrl}/${this.endpointURL}${queryString}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async createVVendorBlacklist(payload) {
    try {
      const response = await this.instance.post(
        `${this.localUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async updateVVendorBlacklist(payload) {
    try {
      const response = await this.instance.patch(
        `${this.localUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class MPOsApiService extends BaseApiService {
  constructor() {
    super('m-pos');
    this.setupInterceptors();
  }
  async fetchPos() {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async getPoById(id) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}/${id}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async createPo(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async getPos(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async getPosWithQueryString(queryString) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}${queryString}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async updatePo(payload) {
    try {
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async generatePoNumber(poId) {
    try {
      let poNumber = '';
      let seed = '00000000';
      poNumber = `PO${(parseInt(seed) + poId)
        .toString()
        .padStart(seed.length, '0')}`;
      return poNumber;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class MPOItemsApiService extends BaseApiService {
  constructor() {
    super('m-po-items');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async getPoItemById(id) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}/${id}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async getPoItems(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async getPoItemByPoId(id) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}?po_id=${id}&$limit=5000`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async createPoItem(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async updatePoItem(payload) {
    try {
      const id = payload.id;
      delete payload['id'];
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${id}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async deletePoItem(id) {
    try {
      const response = await this.instance.delete(
        `${this.serviceUrl}/${this.endpointURL}/${id}`
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class MReceiptQuestionnairesApiService extends BaseApiService {
  constructor() {
    super('m-receipt-questionnaires');
    this.setupInterceptors();
  }

  async fetchQuestionnaires() {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async getReceiptQuestionnaires(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async createReceiptQuestionnaire(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async updateReceiptQuestionnaire(payload) {
    try {
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class MReceiptScoresApiService extends BaseApiService {
  constructor() {
    super('m-receipt-scores');
    this.setupInterceptors();
  }

  async getReceiptScores(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async createReceiptScore(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async updateReceiptScore(payload) {
    try {
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class MPOReceiptsApiService extends BaseApiService {
  constructor() {
    super('m-po-receipts');
    this.setupInterceptors();
  }

  async getPoReceiptById(id) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}/${id}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async getPoReceipts(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async getByQueryString(queryString) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}${queryString}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
      return [];
    }
  }

  async createPoReceipt(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async updatePoReceipt(payload) {
    try {
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async generatePoReceiptNumber(receiptId) {
    try {
      let poReceiptNumber = '';
      let seed = '00000000';
      poReceiptNumber = `RC${(parseInt(seed) + receiptId)
        .toString()
        .padStart(seed.length, '0')}`;
      return poReceiptNumber;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class VPOReceiptsApiService extends BaseApiService {
  constructor() {
    super('v-po-receipts');
    this.setupInterceptors();
  }

  async getPoReceiptById(id) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}/${id}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async getPoReceipts(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async getByQueryString(queryString) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}${queryString}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
      return [];
    }
  }
}
class MPOReceiptItemsApiService extends BaseApiService {
  constructor() {
    super('m-po-receipt-items');
    this.setupInterceptors();
  }

  async getPoReceiptItems(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async createPoReceiptItem(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async updatePoReceiptItem(payload) {
    try {
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class VPOItemRemainingsApiService extends BaseApiService {
  constructor() {
    super('v-po-item-remainings');
    this.setupInterceptors();
  }

  async fetch() {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
      return [];
    }
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async getByQueryString(queryString) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}${queryString}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
      return [];
    }
  }
}

class PoReceiptDatesApiService extends BaseApiService {
  constructor() {
    super('m-po-receipt-dates');
    this.setupInterceptors();
  }

  async getPoReceiptDateById(id) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}/${id}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async getPoReceiptDates(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async getPoReceiptDatesWithQueryString(QueryString) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}${QueryString}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async createPoReceiptDate(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async updatePoReceiptDate(payload) {
    try {
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class PoReceiptDateItemsApiService extends BaseApiService {
  constructor() {
    super('m-po-receipt-date-items');
    this.setupInterceptors();
  }

  async getPoReceiptDateItems(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async createPoReceiptDateItem(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async updatePoReceiptDateItem(payload) {
    try {
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class BlacklistApprovalCommitteesApiService extends BaseApiService {
  constructor() {
    super('bl-approval-committees');
  }

  async getBlApprovalCommittees(params) {
    try {
      const response = await this.instance.get(
        `${this.localUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
          params: params,
        }
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async createBlApprovalCommittees(payload) {
    try {
      const response = await this.instance.post(
        `${this.localUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async updateBlApprovalCommittees(payload) {
    try {
      const response = await this.instance.patch(
        `${this.localUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async getBlApprovalCommitteesById(id) {
    try {
      const response = await this.instance.get(
        `${this.localUrl}/${this.endpointURL}/${id}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class MProductTypesApiService extends BaseApiService {
  constructor() {
    super('m-product-types');
    this.setupInterceptors();
  }

  async fetchProductTypes() {
    const response = await this.instance.get(
      `${this.serviceUrl}/${this.endpointURL}`
    );
    return response.data.data;
  }

  async getProductTypes(params) {
    const response = await this.instance.get(
      `${this.serviceUrl}/${this.endpointURL}`,
      {
        params: params,
        headers: {
          'Cache-Control': 'no-cache',
        },
      }
    );
    return response.data.data;
  }

  async createProductType(payload) {
    const response = await this.instance.post(
      `${this.serviceUrl}/${this.endpointURL}`,
      payload
    );
    return response;
  }

  async updateProductType(payload) {
    const response = await this.instance.patch(
      `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
      payload
    );
    return response;
  }
}

class MUnitsApiService extends BaseApiService {
  constructor() {
    super('m-units');
    this.setupInterceptors();
  }

  async fetchUnits() {
    const response = await this.instance.get(
      `${this.serviceUrl}/${this.endpointURL}?$limit=500`
    );
    return response.data.data;
  }

  async getUnits(params) {
    const response = await this.instance.get(
      `${this.serviceUrl}/${this.endpointURL}`,
      {
        params: params,
        headers: {
          'Cache-Control': 'no-cache',
        },
      }
    );
    return response.data.data;
  }

  async createUnit(payload) {
    const response = await this.instance.post(
      `${this.serviceUrl}/${this.endpointURL}`,
      payload
    );
    return response;
  }

  async updateUnit(payload) {
    const response = await this.instance.patch(
      `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
      payload
    );
    return response;
  }
}

class MCurrenciesApiService extends BaseApiService {
  constructor() {
    super('m-currencies');
    this.setupInterceptors();
  }

  async fetchCurrencies() {
    const response = await this.instance.get(
      `${this.serviceUrl}/${this.endpointURL}?$limit=500`
    );
    return response.data.data;
  }

  async getCurrencies(params) {
    const response = await this.instance.get(
      `${this.serviceUrl}/${this.endpointURL}`,
      {
        params: params,
        headers: {
          'Cache-Control': 'no-cache',
        },
      }
    );
    return response.data.data;
  }

  async createCurrency(payload) {
    const response = await this.instance.post(
      `${this.serviceUrl}/${this.endpointURL}`,
      payload
    );
    return response;
  }

  async updateCurrency(payload) {
    const response = await this.instance.patch(
      `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
      payload
    );
    return response;
  }
}

class MPOBillingsApiService extends BaseApiService {
  constructor() {
    super('m-po-billings');
    this.setupInterceptors();
  }

  async getPoBillings(params) {
    const response = await this.instance.get(
      `${this.serviceUrl}/${this.endpointURL}`,
      {
        params: params,
        headers: {
          'Cache-Control': 'no-cache',
        },
      }
    );
    return response.data;
  }

  async getByQueryString(queryString) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}${queryString}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
      return [];
    }
  }

  async getPoBillingById(id) {
    const response = await this.instance.get(
      `${this.serviceUrl}/${this.endpointURL}/${id}`,
      {
        headers: {
          'Cache-Control': 'no-cache',
        },
      }
    );
    return response;
  }

  async createPoBilling(payload) {
    const response = await this.instance.post(
      `${this.serviceUrl}/${this.endpointURL}`,
      payload
    );
    return response;
  }

  async updatePoBilling(payload) {
    const response = await this.instance.patch(
      `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
      payload
    );
    return response;
  }
}

class MPOBillingItemsApiService extends BaseApiService {
  constructor() {
    super('m-po-billing-items');
    this.setupInterceptors();
  }

  async getPoBillingItems(params) {
    const response = await this.instance.get(
      `${this.serviceUrl}/${this.endpointURL}`,
      {
        params: params,
        headers: {
          'Cache-Control': 'no-cache',
        },
      }
    );
    return response.data;
  }

  async createPoBillingItem(payload) {
    const response = await this.instance.post(
      `${this.serviceUrl}/${this.endpointURL}`,
      payload
    );
    return response;
  }

  async updatePoBillingItem(payload) {
    const response = await this.instance.patch(
      `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
      payload
    );
    return response;
  }
}

class MCostCenterPlantsApiService extends BaseApiService {
  constructor() {
    super('m-cost-center-plants');
    this.setupInterceptors();
  }

  async fetch() {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data.data;
    } catch (err) {
      this.handleErrors(err);
      return [];
    }
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
          params: params,
        }
      );
      return response.data.data;
    } catch (err) {
      this.handleErrors(err);
      return [];
    }
  }

  async getByQueryString(queryString) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}${queryString}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data.data;
    } catch (err) {
      this.handleErrors(err);
      return [];
    }
  }
}

class MPlantsApiService extends BaseApiService {
  constructor() {
    super('m-plants');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
          params: params,
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
      return [];
    }
  }
}

class MVendorPricesApiService extends BaseApiService {
  constructor() {
    super('m-vendor-prices');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
          params: params,
        }
      );
      return response.data.data;
    } catch (err) {
      this.handleErrors(err);
      return [];
    }
  }
}

class MObjectivesApiService extends BaseApiService {
  constructor() {
    super('m-objectives');
    this.setupInterceptors();
  }

  async fetch() {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
          params: params,
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async getById(id) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}/${id}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class MRequestTypesApiService extends BaseApiService {
  constructor() {
    super('m-request-types');
    this.setupInterceptors();
  }

  async fetch() {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
          params: params,
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async getById(id) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}/${id}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async getDefaultByDescription(desc) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}?description=${desc}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data.data[0];
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

/**
 * MeApiService - Returns authenticated user info from backend
 */
class MeApiService extends BaseApiService {
  constructor() {
    super('me');
    this.setupInterceptors();
  }

  async getMe() {
    const response = await this.instance.get(
      `${this.serviceUrl}/${this.endpointURL}`,
      {
        headers: {
          'Cache-Control': 'no-cache',
        },
      }
    );
    return response.data;
  }
}

class MRolesApiService extends BaseApiService {
  constructor() {
    super('m-roles');
    this.setupInterceptors();
  }

  async getRoleById(id) {
    const response = await this.instance.get(
      `${this.serviceUrl}/${this.endpointURL}/${id}`,
      {
        headers: {
          'Cache-Control': 'no-cache',
        },
      }
    );
    return response.data;
  }

  async getRole(params) {
    const response = await this.instance.get(
      `${this.serviceUrl}/${this.endpointURL}`,
      {
        params: params,
        headers: {
          'Cache-Control': 'no-cache',
        },
      }
    );
    return response.data.data;
  }

  async createRole(payload) {
    const response = await this.instance.post(
      `${this.serviceUrl}/${this.endpointURL}`,
      payload
    );
    return response;
  }

  async updateRole(payload) {
    const response = await this.instance.patch(
      `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
      payload
    );
    return response;
  }

  async deleteRole(id) {
    try {
      const response = await this.instance.delete(
        `${this.serviceUrl}/${this.endpointURL}/${id}`
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class MSectionRolesApiService extends BaseApiService {
  constructor() {
    super('m-section-roles');
    this.setupInterceptors();
  }

  async getSectionRole(params) {
    const response = await this.instance.get(
      `${this.serviceUrl}/${this.endpointURL}`,
      {
        params: params,
        headers: {
          'Cache-Control': 'no-cache',
        },
      }
    );
    return response.data.data;
  }

  async createSectionRole(payload) {
    const response = await this.instance.post(
      `${this.serviceUrl}/${this.endpointURL}`,
      payload
    );
    return response;
  }

  async updateSectionRole(payload) {
    const response = await this.instance.patch(
      `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
      payload
    );
    return response;
  }

  async deleteSectionRole(id) {
    try {
      const response = await this.instance.delete(
        `${this.serviceUrl}/${this.endpointURL}/${id}`
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class MZUserRolesApiService extends BaseApiService {
  constructor() {
    super('m-zuser-roles');
    this.setupInterceptors();
  }

  async getUserRole(params) {
    const response = await this.instance.get(
      `${this.serviceUrl}/${this.endpointURL}`,
      {
        params: params,
        headers: {
          'Cache-Control': 'no-cache',
        },
      }
    );
    return response.data.data;
  }

  async createUserRole(payload) {
    const response = await this.instance.post(
      `${this.serviceUrl}/${this.endpointURL}`,
      payload
    );
    return response;
  }

  async updateUserRole(payload) {
    const response = await this.instance.patch(
      `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
      payload
    );
    return response;
  }

  async deleteUserRole(id) {
    try {
      const response = await this.instance.delete(
        `${this.serviceUrl}/${this.endpointURL}/${id}`
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class MContactsApiService extends BaseApiService {
  constructor() {
    super('m-contacts');
    this.setupInterceptors();
  }

  async fetch() {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data.data;
    } catch (err) {
      this.handleErrors(err);
      return [];
    }
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
          params: params,
        }
      );
      return response.data.data;
    } catch (err) {
      this.handleErrors(err);
      return [];
    }
  }
}

class MAddressesApiService extends BaseApiService {
  constructor() {
    super('m-addresses');
    this.setupInterceptors();
  }

  async fetch() {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data.data;
    } catch (err) {
      this.handleErrors(err);
      return [];
    }
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
          params: params,
        }
      );
      return response.data.data;
    } catch (err) {
      this.handleErrors(err);
      return [];
    }
  }
}

class MZUserCostCentersApiService extends BaseApiService {
  constructor() {
    super('m-zuser-cost-centers');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
          params: params,
        }
      );
      return response.data.data;
    } catch (err) {
      this.handleErrors(err);
      return [];
    }
  }

  async getByQueryString(queryString) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}${queryString}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data.data;
    } catch (err) {
      this.handleErrors(err);
      return [];
    }
  }
}

class SystemInformationsApiService extends BaseApiService {
  constructor() {
    super('system-informations');
    this.setupInterceptors();
  }

  async get() {
    const xUID = () => {
      return 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxvxxxxx'.replace(
        /[x]/g,
        function () {
          return ((Math.random() * 16) | 0).toString(16);
        }
      );
    };
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
            'z-request-id': xUID(),
          },
        }
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class WaitingFor1stApproversApiService extends BaseApiService {
  constructor() {
    super('cart-non-cart-waiting-for-1st-approvers');
    this.setupInterceptors();
  }

  async fetch() {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
      return [];
    }
  }
}

class POWithoutPOApprovalsApiService extends BaseApiService {
  constructor() {
    super('po-without-po-approvals');
    this.setupInterceptors();
  }

  async fetch() {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
      return [];
    }
  }
}

class VPOsApiService extends BaseApiService {
  constructor() {
    super('v-pos');
    this.setupInterceptors();
  }
  async fetchPos() {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async getPoById(id) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}/${id}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async createPo(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async getPos(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async getPosWithQueryString(queryString) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}${queryString}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async updatePo(payload) {
    try {
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async generatePoNumber(poId) {
    try {
      let poNumber = '';
      let seed = '00000000';
      poNumber = `PO${(parseInt(seed) + poId)
        .toString()
        .padStart(seed.length, '0')}`;
      return poNumber;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class VPOBillingsApiService extends BaseApiService {
  constructor() {
    super('v-po-billings');
    this.setupInterceptors();
  }

  async getPoBillings(params) {
    const response = await this.instance.get(
      `${this.serviceUrl}/${this.endpointURL}`,
      {
        params: params,
        headers: {
          'Cache-Control': 'no-cache',
        },
      }
    );
    return response.data;
  }

  async getByQueryString(queryString) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}${queryString}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
      return [];
    }
  }

  async getPoBillingById(id) {
    const response = await this.instance.get(
      `${this.serviceUrl}/${this.endpointURL}/${id}`,
      {
        headers: {
          'Cache-Control': 'no-cache',
        },
      }
    );
    return response;
  }

  async createPoBilling(payload) {
    const response = await this.instance.post(
      `${this.serviceUrl}/${this.endpointURL}`,
      payload
    );
    return response;
  }

  async updatePoBilling(payload) {
    const response = await this.instance.patch(
      `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
      payload
    );
    return response;
  }
}

class VNonCartsApiService extends BaseApiService {
  constructor() {
    super('v-non-carts');
    this.setupInterceptors();
  }
  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
          params: params,
        }
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
      return false;
    }
  }

  async getByQueryString(queryString) {
    return this.fetchVNonCartWithFallback(queryString);
  }

  async getNonCarts(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
          params: params,
        }
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async getNonCartWithQueryString(queryString) {
    return this.fetchVNonCartWithFallback(queryString);
  }

  async fetchVNonCartWithFallback(queryString) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}${queryString}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (error) {
      if (this.shouldRetryWithoutIsSourcing(error, queryString)) {
        const fallbackQuery = this.sanitizeIsSourcingQueryParam(queryString);
        if (fallbackQuery !== queryString) {
          try {
            const response = await this.instance.get(
              `${this.serviceUrl}/${this.endpointURL}${fallbackQuery}`,
              {
                headers: {
                  'Cache-Control': 'no-cache',
                },
              }
            );
            return response.data;
          } catch (retryError) {
            this.handleErrors(retryError);
            return false;
          }
        }
      }

      this.handleErrors(error);
      return false;
    }
  }

  shouldRetryWithoutIsSourcing(error, queryString) {
    if (!queryString || !queryString.includes('is_sourcing')) {
      return false;
    }
    const message = error?.response?.data?.message || error?.message || '';
    if (typeof message !== 'string') {
      return false;
    }
    return message.toLowerCase().includes('is_sourcing');
  }

  sanitizeIsSourcingQueryParam(queryString = '') {
    if (!queryString) {
      return queryString;
    }

    let sanitized = queryString.replace(
      /([?&])is_sourcing=[^&]*/gi,
      (match, separator) => (separator === '?' ? '?' : '')
    );

    sanitized = sanitized.replace(/&&+/g, '&');
    sanitized = sanitized.replace(/\?&/, '?');

    if (sanitized.endsWith('&')) {
      sanitized = sanitized.slice(0, -1);
    }
    if (sanitized.endsWith('?')) {
      sanitized = sanitized.slice(0, -1);
    }

    if (sanitized === '?') {
      return '';
    }

    return sanitized;
  }

  async createNonCart(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async updateNonCart(payload) {
    try {
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );

      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async deleteNonCart(id) {
    try {
      const response = await this.instance.delete(
        `${this.serviceUrl}/${this.endpointURL}/${id}`
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class VPRApprovalItemsApiService extends BaseApiService {
  constructor() {
    super('v-pr-approval-items');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
          params: params,
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async getByQueryString(queryString) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}${queryString}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class VPOApprovalItemsApiService extends BaseApiService {
  constructor() {
    super('v-po-approval-items');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
          params: params,
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async getByQueryString(queryString) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}${queryString}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class VNonCartApprovalItemsApiService extends BaseApiService {
  constructor() {
    super('v-non-cart-approval-items');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
          params: params,
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async getByQueryString(queryString) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}${queryString}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class MProductCategoriesApiService extends BaseApiService {
  constructor() {
    super('m-product-categories');
    this.setupInterceptors();
  }

  async getById(id) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}/${id}`
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class MProductSubCategoriesApiService extends BaseApiService {
  constructor() {
    super('m-product-sub-categories');
    this.setupInterceptors();
  }

  async getById(id) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}/${id}`
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class MMaterialGroupsApiService extends BaseApiService {
  constructor() {
    super('m-material-groups');
    this.setupInterceptors();
  }

  async getById(id) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}/${id}`
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class MWBSMaterialGroupsApiService extends BaseApiService {
  constructor() {
    super('m-wbs-material-groups');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class VCatalogsApiService extends BaseApiService {
  constructor() {
    super('v-catalogs');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );

      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async getByQueryString(queryString) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}${queryString}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async create(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );

      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async update(payload) {
    try {
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class VPOReceiptDatesApiService extends BaseApiService {
  constructor() {
    super('v-po-receipt-dates');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );

      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async getByQueryString(queryString) {
    const response = await this.instance.get(
      `${this.serviceUrl}/${this.endpointURL}${queryString}`,
      {
        headers: {
          'Cache-Control': 'no-cache',
        },
      }
    );

    return response.data;
  }

  async create(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );

      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async update(payload) {
    try {
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class MPaymentApprovalsApiService extends BaseApiService {
  constructor() {
    super('m-payment-approvals');
    this.setupInterceptors();
  }
  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async create(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async update(payload) {
    try {
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class MPaymentApprovalItemsApiService extends BaseApiService {
  constructor() {
    super('m-payment-approval-items');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async create(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async update(payload) {
    try {
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class MPaymentApprovalCommentsApiService extends BaseApiService {
  constructor() {
    super('m-payment-approval-comments');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async create(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async update(payload) {
    try {
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class VPaymentApprovalItemsApiService extends BaseApiService {
  constructor() {
    super('v-payment-approval-items');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async getByQueryString(queryString) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}${queryString}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
      return [];
    }
  }
}

class MSourcingApprovalsApiService extends BaseApiService {
  constructor() {
    super('m-sourcing-approvals');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async create(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async update(payload) {
    try {
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class MSourcingApprovalItemsApiService extends BaseApiService {
  constructor() {
    super('m-sourcing-approval-items');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async getByQueryString(queryString) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}${queryString}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
      return [];
    }
  }

  async create(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async update(payload) {
    try {
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class MSourcingApprovalCommentsApiService extends BaseApiService {
  constructor() {
    super('m-sourcing-approval-comments');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async create(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async update(payload) {
    try {
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class VSourcingApprovalItemsApiService extends BaseApiService {
  constructor() {
    super('v-sourcing-approval-items');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async getByQueryString(queryString) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}${queryString}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
      return [];
    }
  }
}

class MBranchesApiService extends BaseApiService {
  constructor() {
    super('m-branches');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class MTrackingStatusesApiService extends BaseApiService {
  constructor() {
    super('m-tracking-statuses');
    this.setupInterceptors();
  }
  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}
class VOperationReportsApiService extends BaseApiService {
  constructor() {
    super('v-operation-reports');
    this.setupInterceptors();
  }
  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}
class VOperationTrackingsApiService extends BaseApiService {
  constructor() {
    super('v-operation-trackings');
    this.setupInterceptors();
  }
  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class MQuestionnairesApiService extends BaseApiService {
  constructor() {
    super('m-questionnaires');
    this.setupInterceptors();
  }

  async get(params) {
    const response = await this.instance.get(
      `${this.serviceUrl}/${this.endpointURL}`,
      {
        params: params,
        headers: {
          'Cache-Control': 'no-cache',
        },
      }
    );

    return response.data;
  }

  async create(payload) {
    const response = await this.instance.post(
      `${this.serviceUrl}/${this.endpointURL}`,
      payload
    );

    return response;
  }

  async update(payload) {
    const response = await this.instance.patch(
      `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
      payload
    );

    return response;
  }
}

class VQuestionnairesApiService extends BaseApiService {
  constructor() {
    super('v-questionnaires');
    this.setupInterceptors();
  }

  // async get(params) {
  //   try {
  //     const response = await this.instance.get(`${this.serviceUrl}/${this.endpointURL}`, {
  //       params: params,
  //     });
  //     return response.data;
  //   } catch (err) {
  //     this.handleErrors(err);
  //   }
  // }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.localUrl}/${this.endpointURL}`,
        {
          params: params,
        }
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class MQuestionTypesApiService extends BaseApiService {
  constructor() {
    super('m-question-types');
    this.setupInterceptors();
  }

  // async get(params) {
  //   try {
  //     const response = await this.instance.get(`${this.serviceUrl}/${this.endpointURL}`, {
  //       params: params,
  //     });
  //     return response.data;
  //   } catch (err) {
  //     this.handleErrors(err);
  //   }
  // }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.localUrl}/${this.endpointURL}`,
        {
          params: params,
        }
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class MReportsApiService extends BaseApiService {
  constructor() {
    super('m-reports');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class MVendorCategoriesApiService extends BaseApiService {
  constructor() {
    super('m-vendor-categories');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class MVendorRatingsApiService extends BaseApiService {
  constructor() {
    super('m-vendor-ratings');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class VPrequalifiedVendorsApiService extends BaseApiService {
  constructor() {
    super('v-prequalified-vendors');
    this.setupInterceptors();
  }

  async get(params, signal) {
    const response = await this.instance.get(
      `${this.serviceUrl}/${this.endpointURL}`,
      {
        params: params,
        signal: signal,
      }
    );

    return response.data;
  }
}

class PPrequalifiedVendorRatingsApiService extends BaseApiService {
  constructor() {
    super('p-prequalified-vendor-ratings');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class VGRQuestionnaireVendorsApiService extends BaseApiService {
  constructor() {
    super('v-gr-questionnaire-vendors');
    this.setupInterceptors();
  }

  async get(params, signal) {
    const response = await this.instance.get(
      `${this.serviceUrl}/${this.endpointURL}`,
      {
        params: params,
        signal: signal,
      }
    );

    return response.data;
  }
}

class PGRQuestionnaireRatingsApiService extends BaseApiService {
  constructor() {
    super('p-gr-questionnaire-ratings');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class ModuleSettingsApiService extends BaseApiService {
  constructor() {
    super('module-settings');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        { params: params }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class AFCTicketsApiService extends BaseApiService {
  constructor() {
    super('afc-tickets');
    this.setupInterceptors();
  }

  async getTicket() {
    try {
      const sysInstance = new SystemInformationsApiService();
      const sysInfoResponse = await sysInstance.get();
      const pv = sysInfoResponse?.data[0]?.pv;

      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          pv: CryptoJS.MD5(pv).toString(),
        }
      );

      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class PMailsApiService extends BaseApiService {
  constructor() {
    super('p-mails');
    this.setupInterceptors();
  }

  async create(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class ExchangeRatesApiService extends BaseApiService {
  constructor() {
    super('exchange-rates');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class BillingsApiService extends BaseApiService {
  constructor() {
    super('billings');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async getById(id) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}/${id}`
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async getByQueryString(queryString) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}${queryString}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async create(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async update(payload) {
    try {
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class BillingItemsApiService extends BaseApiService {
  constructor() {
    super('billing-items');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async getByQueryString(queryString) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}${queryString}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async create(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async update(payload) {
    try {
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class ReasonsApiService extends BaseApiService {
  constructor() {
    super('reasons');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async create(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async update(payload) {
    try {
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload
      );
      return response;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class VBillingsApiService extends BaseApiService {
  constructor() {
    super('v-billings');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async getById(id) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}/${id}`
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }
  async getByQueryString(queryString) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}${queryString}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class VBillingItemsApiService extends BaseApiService {
  constructor() {
    super('v-billing-items');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async getByQueryString(queryString) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}${queryString}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class VUnselectBillingsApiService extends BaseApiService {
  constructor() {
    super('v-unselect-billings');
    this.setupInterceptors();
  }

  async get(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params: params,
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async getByQueryString(queryString) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}${queryString}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class DelegateTransactionsApiService extends BaseApiService {
  constructor() {
    super('delegate-transactions');
    this.setupInterceptors();
  }

  async fetchDocuments(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async searchTransferredOut(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}/transferred-out`,
        {
          params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async searchTransferredIn(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}/transferred-in`,
        {
          params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async delegate(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}`,
        payload
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

class SpPOForSupportingsApiService extends BaseApiService {
  constructor() {
    super('sp-po-for-supportings');
    this.setupInterceptors();
  }

  async cancel(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload,
        {
          headers: {
            'x-service-method': 'cancel',
          },
        }
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async close(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload,
        {
          headers: {
            'x-service-method': 'close',
          },
        }
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }

  async changeDeliveryDate(payload) {
    try {
      const response = await this.instance.post(
        `${this.serviceUrl}/${this.endpointURL}/${payload.id}`,
        payload,
        {
          headers: {
            "x-service-method": "change_delivery_date",
          },
        }
      );
      return response;
    } catch (err) {
      this.handleErrors(err);
    }
  }
}

class SpClosePoApiService extends BaseApiService {
  constructor() {
    super('sp-close-po');
    this.setupInterceptors();
  }

  async find(params) {
    try {
      const response = await this.instance.get(
        `${this.serviceUrl}/${this.endpointURL}`,
        {
          params,
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }

  async close(poId) {
    try {
      const response = await this.instance.patch(
        `${this.serviceUrl}/${this.endpointURL}/${poId}`
      );
      return response.data;
    } catch (error) {
      this.handleErrors(error);
    }
  }
}

const delegateTransactionsService = new DelegateTransactionsApiService();

export const $api = {
  m_carts: new MCartsApiService(),
  m_cart_items: new MCartItemsApiService(),
  m_pr_approvals: new MPRApprovalsApiService(),
  m_pr_approval_items: new MPRApprovalItemsApiService(),
  m_pr_approval_comments: new MPRApprovalCommentsApiService(),
  m_po_approvals: new MPOApprovalsApiService(),
  m_po_approval_items: new MPOApprovalItemsApiService(),
  m_po_approval_comments: new MPOApprovalCommentsApiService(),
  m_po_cancel_approvals: new MPOCancelApprovalsApiService(),
  m_po_cancel_approval_items: new MPOCancelApprovalItemsApiService(),
  m_po_cancel_approval_comments: new MPOCancelApprovalCommentsApiService(),
  m_non_cart_approvals: new MNonCartApprovalsApiService(),
  m_non_cart_approval_items: new MNonCartApprovalItemsApiService(),
  m_non_cart_approval_comments: new MNonCartApprovalCommentsApiService(),
  m_payment_approvals: new MPaymentApprovalsApiService(),
  m_payment_approval_items: new MPaymentApprovalItemsApiService(),
  m_payment_approval_comments: new MPaymentApprovalCommentsApiService(),
  v_payment_approval_items: new VPaymentApprovalItemsApiService(),
  m_sourcing_approvals: new MSourcingApprovalsApiService(),
  m_sourcing_approval_items: new MSourcingApprovalItemsApiService(),
  m_sourcing_approval_comments: new MSourcingApprovalCommentsApiService(),
  v_sourcing_approval_items: new VSourcingApprovalItemsApiService(),
  m_non_carts: new MNonCartsApiService(),
  m_non_cart_items: new MNonCartItemsApiService(),
  m_non_cart_vendors: new MNonCartVendorsApiService(),
  m_departments: new MDepartmentsApiService(),
  m_product_gl_accounts: new MProductGLAccountsApiService(),
  employee: new EmployeeApiService(),
  vendor_informations: new VendorInformationsApiService(),
  approver_list: new ApproverListApiService(),
  m_v_product_categories: new MVProductCategoriesApiService(),
  m_v_product_categories_expired: new MVProductCategoriesExpiredApiService(),
  m_v_product_details: new MVProductDetailsApiService(),
  v_product_media: new VProductMediaApiService(),
  product_images: new ProductImagesApiService(),
  m_product_favorites: new MProductFavoritesApiService(),
  m_product_recently_views: new MProductRecentlyViewsApiService(),
  e_memo: new EMemoApiService(),
  e_marketing: new EMarketing(),
  v_cost_center_budgets: new VCostCenterBudgetApiService(),
  m_wbses: new MWbsesApiService(),
  v_wbs_budgets: new VWBSBudgetApiService(),
  m_alfresco_procurements: new MAlfrescoProcurementsApiService(),
  vendor_boards: new VendorBoardsApiService(),
  blacklist_conditions: new BlacklistConditionsApiService(),
  vendor_blacklists: new VendorBlacklistsApiService(),
  v_vendor_blacklists: new VVendorBlacklistsApiService(),
  po: new MPOsApiService(),
  po_items: new MPOItemsApiService(),
  m_receipt_questionnaires: new MReceiptQuestionnairesApiService(),
  m_receipt_scores: new MReceiptScoresApiService(),
  m_po_receipts: new MPOReceiptsApiService(),
  v_po_receipts: new VPOReceiptsApiService(),
  m_po_receipt_items: new MPOReceiptItemsApiService(),
  m_po_billings: new MPOBillingsApiService(),
  m_po_billing_items: new MPOBillingItemsApiService(),
  po_receipt_dates: new PoReceiptDatesApiService(),
  po_receipt_date_items: new PoReceiptDateItemsApiService(),
  emailing: new EmailingApiService(),
  upload_catalog: new UploadCatalogApiService(),
  bl_approval_committees: new BlacklistApprovalCommitteesApiService(),
  m_product_types: new MProductTypesApiService(),
  m_units: new MUnitsApiService(),
  m_currencies: new MCurrenciesApiService(),
  m_cost_center_plants: new MCostCenterPlantsApiService(),
  m_plants: new MPlantsApiService(),
  m_vendor_prices: new MVendorPricesApiService(),
  m_objectives: new MObjectivesApiService(),
  m_request_types: new MRequestTypesApiService(),
  m_roles: new MRolesApiService(),
  m_section_roles: new MSectionRolesApiService(),
  m_zuser_roles: new MZUserRolesApiService(),
  m_zuser_cost_centers: new MZUserCostCentersApiService(),
  m_contacts: new MContactsApiService(),
  m_addresses: new MAddressesApiService(),
  system_informations: new SystemInformationsApiService(),
  waiting_for_1st_approvers: new WaitingFor1stApproversApiService(),
  po_without_po_approvals: new POWithoutPOApprovalsApiService(),
  v_po_item_remainings: new VPOItemRemainingsApiService(),
  v_pos: new VPOsApiService(),
  v_po_billings: new VPOBillingsApiService(),
  v_non_carts: new VNonCartsApiService(),
  v_pr_approval_items: new VPRApprovalItemsApiService(),
  v_po_approval_items: new VPOApprovalItemsApiService(),
  v_non_cart_approval_items: new VNonCartApprovalItemsApiService(),
  m_product_categories: new MProductCategoriesApiService(),
  m_product_sub_categories: new MProductSubCategoriesApiService(),
  m_material_groups: new MMaterialGroupsApiService(),
  m_wbs_material_groups: new MWBSMaterialGroupsApiService(),
  v_vendor_branches: new VVendorBranchesApiService(),
  v_payment_informations: new VPaymentInformationsApiService(),
  v_vendor_invoices: new VVendorInvoicesApiService(),
  v_catalogs: new VCatalogsApiService(),
  v_po_receipt_dates: new VPOReceiptDatesApiService(),
  m_branches: new MBranchesApiService(),
  m_tracking_statuses: new MTrackingStatusesApiService(),
  v_operation_reports: new VOperationReportsApiService(),
  v_operation_trackings: new VOperationTrackingsApiService(),
  m_questionnaires: new MQuestionnairesApiService(),
  v_questionnaires: new VQuestionnairesApiService(),
  m_question_types: new MQuestionTypesApiService(),
  m_reports: new MReportsApiService(),
  m_vendor_categories: new MVendorCategoriesApiService(),
  m_vendor_ratings: new MVendorRatingsApiService(),
  v_prequalified_vendors: new VPrequalifiedVendorsApiService(),
  p_prequalified_vendor_ratings: new PPrequalifiedVendorRatingsApiService(),
  v_gr_questionnaire_vendors: new VGRQuestionnaireVendorsApiService(),
  p_gr_questionnaire_ratings: new PGRQuestionnaireRatingsApiService(),
  module_settings: new ModuleSettingsApiService(),
  afc_tickets: new AFCTicketsApiService(),
  p_mails: new PMailsApiService(),
  exchange_rates: new ExchangeRatesApiService(),
  billings: new BillingsApiService(),
  billing_items: new BillingItemsApiService(),
  reasons: new ReasonsApiService(),
  v_billings: new VBillingsApiService(),
  v_billing_items: new VBillingItemsApiService(),
  v_unselect_billings: new VUnselectBillingsApiService(),
  delegate_transactions: delegateTransactionsService,
  delegateTransactions: delegateTransactionsService,
  sp_po_for_supportings: new SpPOForSupportingsApiService(),
  sp_close_po: new SpClosePoApiService(),
  me: new MeApiService(),
};
